'use client'
import React, { useMemo, useState, useEffect } from 'react'
import Link from 'next/link'
import StoreMap from './StoreMap'
import { usePathname } from 'next/navigation'
// 與 Collections 一致的裝置判斷（桌機/手機可帶同一張）
import { GetImagePathByDevice } from '@imageUtils'
import styles from './StoreLocator.module.css'
import Image from 'next/image'
import { Select, Drawer } from 'antd'
import { DownOutlined, UpOutlined } from '@ant-design/icons'

/** 將 URL 清理、強制 https，避免混合內容被瀏覽器擋掉 */
function safeUrl(u) {
  if (!u || typeof u !== 'string') return ''
  const s = u.trim()
  if (!s) return ''
  return s.startsWith('http://') ? s.replace('http://', 'https://') : s
}

/** 把 API 轉成 UI 需要的結構（圖片對齊你的 Collections 寫法） */
function mapToUILocations(list = []) {
  return list.map((s) => {
    const main = safeUrl(
      s.MainPicturePath || s.PictureUrl || s.PictureURL || ''
    )
    return {
      id: s.OfflineStoreNumber,
      title: s.OfflineStoreName,
      city: s.City || '',
      address: s.Address,
      country: '',
      phone: [s.TelAreaCode, s.Tel].filter(Boolean).join('-'),
      hours:
        s.OpeningHoursRemark && s.OpeningHoursRemark.trim() !== ''
          ? s.OpeningHoursRemark
          : `平日 ${s.WeekdayStartTime} - ${s.WeekdayEndTime} / 假日 ${s.WeekendStartTime} - ${s.WeekendEndTime}`,
      // 與 Collections 一致的欄位：桌機/手機都給同一張 main
      PictureByDevice: s.PictureByDevice ?? '',
      IsCompressPicture: false,
      ComputerPicturePath: main,
      MobilePicturePath: main,
      SupportCreditCard: s.SupportCreditCard,
      SupportTravelCard: s.SupportTravelCard,
      SupportParking: s.SupportParking,
      SupportWiFi: s.SupportWiFi,
      raw: s,
      fullAdress: s.City + s.DistrictID + s.Address,
    }
  })
}

const buildFilterQS = (city, area) => {
  const qs = new URLSearchParams()
  if (city) qs.set('CityID', String(city))
  if (area) qs.set('DistrictID', String(area))

  return qs
}

const selectCity = async (value, setAreaOption, setList) => {
  const city = value
  if (city === '') return
  const qs = buildFilterQS(city)
  await fetch(
    `/api/offline/get_OfflineCityAndAreaOption?${qs.toString()}`
  ).then(async (res) => {
    const json = await res.json()
    const data = json?.Data
    const newData = [{ value: null, label: '全區' }, ...data]
    setAreaOption(newData)
  })

  await fetch(`/api/offline/get_OfflineStoreList?${qs.toString()}`).then(
    async (res) => {
      const json = await res.json()
      setList(json?.Data.StoreData)
    }
  )
}

const selectArea = async (city, value, setList) => {
  const area = value
  const qs = buildFilterQS(city, area)
  await fetch(`/api/offline/get_OfflineStoreList?${qs.toString()}`).then(
    async (res) => {
      const json = await res.json()
      setList(json?.Data.StoreData)
    }
  )
}

const getChinese = (data, option) => {
  if (data && option) {
    const name = option.find((o) => o.value === data).label
    return name
  }
  return ''
}

const storeView = (
  isMobile,
  filtered,
  pathname,
  cityOption,
  areaOption,
  setAreaOption,
  city,
  area,
  setCity,
  setArea,
  setList,
  setOpenDrawer,
  source
) => {
  // 檢查從哪裡進來給不同畫面
  const basePath =
    source && source === 'line'
      ? '/store_location_for_line/'
      : '/store_location/'

  return (
    <div className="row">
      <div>
        <form onSubmit={(e) => e.preventDefault()}>
          {/* 🔥 上方條件區 */}
          <div
            className="d-flex mb-2 pt-3 pb-3"
            style={{
              gap: '8px',
            }}
          >
            {/* PC 才顯示標題 */}
            {!isMobile && (
              <span style={{ fontSize: '18px' }} className="me-3">
                搜尋門市
              </span>
            )}

            {/* 🔥 PC 用 Select */}
            {!isMobile && (
              <>
                <Select
                  options={cityOption}
                  placeholder="選擇城市"
                  onChange={(value) => {
                    setCity(value)
                    setArea(null)
                    selectCity(value, setAreaOption, setList)
                  }}
                  style={{ width: 200 }}
                  value={city}
                />

                <Select
                  options={areaOption}
                  placeholder="選擇區縣"
                  onChange={(value) => {
                    setArea(value)
                    selectArea(city, value, setList)
                  }}
                  style={{ width: 200 }}
                  disabled={!city}
                  value={area}
                />
              </>
            )}

            {/* 🔥 Mobile 用假 Select */}
            {isMobile && (
              <div
                onClick={() => setOpenDrawer(true)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '8px',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                {city || area
                  ? `${getChinese(city, cityOption) || ''} ${getChinese(area, areaOption) || ''}`
                  : '選擇城市 / 區域'}
              </div>
            )}
          </div>

          {/* 🔥 門市列表 */}
          <div>
            {filtered.map((elm) => {
              const bgUrl =
                GetImagePathByDevice({
                  PictureByDevice: elm.PictureByDevice,
                  ComputerPicturePath: elm.ComputerPicturePath,
                  MobilePicturePath: elm.MobilePicturePath,
                }) || ''
              return (
                <Link href={`${basePath}${elm.id}`} key={elm.id}>
                  <div
                    className={`${styles.storeCard} mb-2`}
                    style={{ padding: '10px' }}
                  >
                    <div className="d-flex gap-3 align-items-center">
                      {bgUrl && (
                        <div
                          style={{
                            width: isMobile ? 100 : 120,
                            height: isMobile ? 75 : 90,
                            background: 'black',
                            overflow: 'hidden',
                          }}
                        >
                          <Image
                            src={bgUrl}
                            width={120}
                            height={90}
                            preview={false}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            alt=""
                          />
                        </div>
                      )}

                      <div className="flex-grow-1">
                        <h5 style={{ fontSize: '15px' }}>
                          {pathname === '/store_location_for_line' ? (
                            elm.title
                          ) : (
                            <span className={styles.titleDiv}>{elm.title}</span>
                          )}
                        </h5>

                        <div className="d-flex flex-column">
                          {elm.address && (
                            <a
                              // href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(elm.fullAdress)}`}
                              style={{ fontSize: '12px' }}
                            >
                              {elm.fullAdress}
                            </a>
                          )}
                          {elm.phone && (
                            <a
                              // href={`tel:${elm.phone.replace(/-/g, '')}`}
                              style={{ fontSize: '12px' }}
                            >
                              {elm.phone}
                            </a>
                          )}
                        </div>

                        <div className={styles.badges}>
                          {!!Number(elm.SupportCreditCard) && (
                            <span className="badge bg-secondary me-2">
                              可刷卡
                            </span>
                          )}
                          {!!Number(elm.SupportTravelCard) && (
                            <span className="badge bg-secondary me-2">
                              國旅卡
                            </span>
                          )}
                          {!!Number(elm.SupportWiFi) && (
                            <span className="badge bg-secondary me-2">
                              Wi-Fi
                            </span>
                          )}
                          {!!Number(elm.SupportParking) && (
                            <span className="badge bg-secondary me-2">
                              停車位
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              )
            })}

            {!filtered.length && (
              <p style={{ padding: 12 }}>No stores found.</p>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default function StoreLocator({ initialStores = [], source }) {
  const pathname = usePathname()
  const [getLocation, setLocation] = useState(null)
  const [keyword, setKeyword] = useState('')
  const [list, setList] = useState(initialStores)

  const [isMobile, setIsMobile] = useState(false)

  const [cityOption, setCityOption] = useState([])
  const [areaOption, setAreaOption] = useState([])

  const [city, setCity] = useState(null)
  const [area, setArea] = useState(null)

  const [openDrawer, setOpenDrawer] = useState(false)
  const [activeCity, setActiveCity] = useState(null)

  const uiStores = useMemo(() => mapToUILocations(list), [list])

  const qs = buildFilterQS()
  const getCityOption = async () => {
    await fetch(
      `/api/offline/get_OfflineCityAndAreaOption?${qs.toString()}`
    ).then(async (res) => {
      const json = await res.json()

      setCityOption([{ value: '', label: '全部門市' }, ...json?.Data])
    })
  }

  const filtered = useMemo(() => {
    const k = keyword.trim().toLowerCase()
    if (!k) return uiStores
    return uiStores.filter((s) =>
      `${s.city} ${s.address} ${s.title}`.toLowerCase().includes(k)
    )
  }, [keyword, uiStores])

  useEffect(() => {
    getCityOption()
  }, [])

  useEffect(() => {
    const check = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    check() // 初始判斷
    window.addEventListener('resize', check)

    return () => window.removeEventListener('resize', check)
  }, [])

  return (
    <section className="store-location">
      {storeView(
        isMobile,
        filtered,
        pathname,
        cityOption,
        areaOption,
        setAreaOption,
        city,
        area,
        setCity,
        setArea,
        setList,
        setOpenDrawer,
        source
      )}

      {/* 圖片樣式（與 Collections 一致：背景圖 cover/center） */}
      <style jsx>{`
        .store-thumb {
          width: 260px;
          height: 170px;
          background-color: #f7f7f7;
          background-size: cover;
          background-position: center center;
          background-repeat: no-repeat;
          overflow: hidden;
          flex: 0 0 260px;
        }
        @media (max-width: 576px) {
          .store-thumb {
            width: 160px;
            height: 110px;
            flex-basis: 160px;
          }
        }
      `}</style>

      <Drawer
        title="選擇門市"
        placement="bottom"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        height="70%"
      >
        {cityOption.length > 0 &&
          cityOption.map((c) => {
            const isActive = activeCity === c.value
            const isAll = !c.value // 👈 關鍵（value === ''）

            return (
              <div
                key={c.value}
                style={{
                  borderRadius: '12px',
                  background: '#f7f7f7',
                  padding: '12px',
                  marginBottom: '12px',
                }}
              >
                {/* 🔥 城市標題 */}
                <div
                  onClick={async () => {
                    // 👇 如果是「全部」
                    if (isAll) {
                      setCity(null)
                      setArea(null)
                      setActiveCity(null)
                      setList(initialStores) // 👈 回全部資料
                      setOpenDrawer(false)
                      return
                    }

                    // 👇 正常城市
                    if (isActive) {
                      setActiveCity(null)
                      return
                    }

                    setActiveCity(c.value)
                    setCity(c.value)
                    setArea(null)

                    await selectCity(c.value, setAreaOption, setList)
                  }}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  <span>{c.label}</span>

                  {/* 🔥 只有不是「全部」才顯示箭頭 */}
                  {!isAll && (isActive ? <UpOutlined /> : <DownOutlined />)}
                </div>

                {/* 🔥 區域（全部不會進來） */}
                {!isAll && isActive && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '10px',
                      marginTop: '12px',
                    }}
                  >
                    {areaOption.map((a) => (
                      <div
                        key={a.value}
                        onClick={() => {
                          setArea(a.value)
                          selectArea(c.value, a.value, setList)
                          setOpenDrawer(false)
                        }}
                        style={{
                          padding: '10px',
                          textAlign: 'center',
                          borderRadius: '10px',
                          background: area === a.value ? '#b4c6f7' : '#e5e7eb',
                          cursor: 'pointer',
                          fontSize: '14px',
                        }}
                      >
                        {a.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
      </Drawer>
    </section>
  )
}
